import type { ChartDataPoint } from '../composites/chart-types';

/**
 * Shared constants and utilities for chart axis label rendering.
 * All charts use dynamic sizing and truncation to handle varying label lengths
 * and category counts while preventing overlap.
 */

/** Base tick text wrapping width (px) for compact horizontal category axes */
const BASE_TICK_LABEL_WIDTH = 110;

/** Base axis gutter width (px) for horizontal category axes */
const BASE_AXIS_WIDTH = 120;

/** Approximate pixels per character at fontSize 12 for width calculation */
const PIXELS_PER_CHAR = 9.5;

/** Maximum character length before truncating labels with ellipsis */
export const MAX_LABEL_LENGTH_BEFORE_TRUNCATION = 30;

/** Maximum axis width in pixels to prevent excessive horizontal space */
const MAX_AXIS_WIDTH = 280;

/** Height factor for rotated labels at fontSize 12: ~7px/char * sin(45°) ≈ 5px/char */
const ROTATED_LABEL_HEIGHT_FACTOR = 5;

/** Approximate pixels per character at fontSize 12 for horizontal label width */
const HORIZONTAL_LABEL_PIXELS_PER_CHAR = 7;

/**
 * Determine if vertical chart X-axis labels need rotation based on label lengths
 * and available horizontal space. Labels are rotated when they would overlap
 * if displayed horizontally.
 *
 * @param data - Chart data rows
 * @param categoryKey - Key in each row used for category axis labels
 * @returns true if labels should be rotated -45°
 */
export function shouldRotateXAxisLabels(
  data: ChartDataPoint[],
  categoryKey: string,
): boolean {
  if (!data.length) return false;

  // Get the longest label length
  const maxLabelLength = Math.max(
    ...data.map((item) => String(item[categoryKey] ?? '').length),
  );

  // Estimate horizontal space per label (assumes relatively even distribution)
  // At fontSize 12, each character is ~7px wide. Add some padding.
  const estimatedLabelWidth = maxLabelLength * HORIZONTAL_LABEL_PIXELS_PER_CHAR;
  const categoryCount = data.length;

  // Rough estimate: if we have many categories or long labels, they'll overlap
  // This heuristic checks if labels would take more than ~80% of available space
  // assuming reasonable chart width (~600px minimum)
  const minChartWidth = 600;
  const availableSpacePerLabel = minChartWidth / categoryCount;

  return estimatedLabelWidth > availableSpacePerLabel * 0.8;
}

/**
 * Calculate dynamic axis width and tick label width based on the longest
 * category label. This prevents label truncation/wrapping by adapting to
 * the actual data. Width is capped to keep charts focused on data; labels
 * exceeding ~30 chars should be truncated.
 *
 * @param data - Chart data rows
 * @param categoryKey - Key in each row used for category axis labels
 * @returns Object with tickWidth and axisWidth in pixels (max: 280px)
 *
 * @example
 * // Short labels (10-15 chars)
 * getDynamicAxisWidth(data, 'category') // { tickWidth: 110, axisWidth: 130 }
 *
 * @example
 * // Medium labels (25 chars)
 * getDynamicAxisWidth(data, 'category') // { tickWidth: 187.5, axisWidth: 207.5 }
 *
 * @example
 * // Long labels (45 chars) - capped
 * getDynamicAxisWidth(data, 'category') // { tickWidth: 260, axisWidth: 280 }
 */
export function getDynamicAxisWidth(
  data: ChartDataPoint[],
  categoryKey: string,
): { tickWidth: number; axisWidth: number } {
  if (data.length === 0) {
    return { tickWidth: BASE_TICK_LABEL_WIDTH, axisWidth: BASE_AXIS_WIDTH };
  }

  // Find the longest label (with fallback of 0 for all-null/undefined labels)
  const maxLabelLength = Math.max(
    0,
    ...data.map((item) => String(item[categoryKey] ?? '').length),
  );

  // Calculate width needed based on character count
  // At fontSize 12, approximately 10 pixels per character
  const calculatedWidth = Math.max(
    BASE_TICK_LABEL_WIDTH,
    maxLabelLength * PIXELS_PER_CHAR,
  );

  // Cap at MAX_AXIS_WIDTH to prevent excessive horizontal space - extremely
  // long labels should be truncated by the formatter instead
  const tickWidth = Math.min(calculatedWidth, MAX_AXIS_WIDTH - 30);
  const axisWidth = Math.min(tickWidth + 30, MAX_AXIS_WIDTH); // Add 30px gutter for breathing room

  return { tickWidth, axisWidth };
}

/**
 * Calculate dynamic X-axis height for vertical bar charts with rotated labels.
 * When labels are rotated -45°, longer labels need more vertical space to avoid
 * overlapping with the legend or getting cut off. Height is capped at 140px to
 * keep charts focused on data; labels exceeding ~30 chars should be truncated.
 *
 * @param data - Chart data rows
 * @param categoryKey - Key in each row used for category axis labels
 * @param hasAxisLabel - Whether the axis has a label (needs extra space)
 * @returns Height in pixels needed for the X-axis (min: 30-50px, max: 140px)
 *
 * @example
 * // Short labels (10 chars) without axis label
 * getDynamicXAxisHeight(data, 'category', false) // ~73
 *
 * @example
 * // Medium labels (20 chars) with axis label
 * getDynamicXAxisHeight(data, 'category', true) // ~135
 */
export function getDynamicXAxisHeight(
  data: ChartDataPoint[],
  categoryKey: string,
  hasAxisLabel: boolean,
): number {
  if (data.length === 0) {
    return hasAxisLabel ? 50 : 30;
  }

  // Find the longest label (with fallback of 0 for all-null/undefined labels)
  const maxLabelLength = Math.max(
    0,
    ...data.map((item) => String(item[categoryKey] ?? '').length),
  );

  // For rotated labels at -45°, the vertical projection is approximately
  // labelWidth * sin(45°) ≈ labelWidth * 0.707
  // At fontSize 12, each character is ~7px wide, so vertical space needed is:
  // maxLabelLength * 7px * 0.707 ≈ maxLabelLength * 5px
  const baseHeight = 30; // Minimum for tick marks and padding
  const labelHeight = Math.ceil(maxLabelLength * ROTATED_LABEL_HEIGHT_FACTOR);
  const axisLabelSpace = hasAxisLabel ? 30 : 0; // Extra space for axis label below rotated labels

  // Total height with reasonable min/max bounds
  // Cap at 160px to prevent excessive vertical space - extremely long labels
  // should be truncated by the formatter instead
  const calculatedHeight = baseHeight + labelHeight + axisLabelSpace;
  return Math.max(hasAxisLabel ? 50 : 30, Math.min(calculatedHeight, 160));
}

/**
 * Truncate a string to a maximum length and add ellipsis if needed.
 * Useful for chart labels that would otherwise be too long.
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum character length before truncation (must be >= 4)
 * @returns Truncated string with ellipsis if needed
 *
 * @example
 * truncateWithEllipsis('Short', 20) // 'Short'
 * truncateWithEllipsis('This is a very long label', 15) // 'This is a ve...'
 */
export function truncateWithEllipsis(text: string, maxLength: number): string {
  // Boundary check: if maxLength is too small or text fits, return as-is
  if (maxLength < 4 || text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Calculate the right margin (px) needed on a Recharts chart to prevent the
 * last X-axis tick label from being clipped by the SVG viewport.
 *
 * - **Rotated labels** swing to the right past the tick point so they need a
 *   fixed buffer (~24 px) for the descending text.
 * - **Non-rotated (centered) labels** have their right half hanging past the
 *   last tick, so the margin must be at least half the last label's pixel
 *   width. We use `HORIZONTAL_LABEL_PIXELS_PER_CHAR` and clamp to [8, 64].
 *
 * @param data          - Chart data rows
 * @param categoryKey   - Key used for the X-axis category value
 * @param needsRotation - Whether labels are rendered at -45°
 */
export function getXAxisRightMargin(
  data: ChartDataPoint[],
  categoryKey: string,
  needsRotation: boolean,
): number {
  if (needsRotation) return 24;
  if (!data.length) return 8;
  const lastLabel = String(data[data.length - 1][categoryKey] ?? '');
  const halfLabelWidth = Math.ceil(
    (lastLabel.length * HORIZONTAL_LABEL_PIXELS_PER_CHAR) / 2,
  );
  return Math.max(8, Math.min(halfLabelWidth, 64));
}
